import { getTagFromNode, useApiClient, useTranslation } from '@msdk/common'
import {
  AttachmentFieldDefinition,
  AttachmentValue,
  ControlValidationType,
  FormFieldDefinition,
  FormOpenMode,
} from '@msdk/definitions'
import {
  ButtonIcon,
  CloseIcon,
  FileDownloadOutlinedIcon,
  FileUploadOutlinedIcon,
  FormField,
  FormFieldLabel,
  InputAdornment,
  TextInput,
} from '@msdk/ui'
import React, { ChangeEvent, useRef } from 'react'
import { useParams } from 'react-router-dom'

import { useFieldLabel, useFieldSeverity, useFieldValue, useHasMandatoryValidation } from '../../../hooks'
import { FieldProps } from '../../../models/form'
import { nodeHasFlag } from '../../../utils'
import { downloadAttachment } from '../../../utils/attachment'
import { ValidationMessages } from '../../ValidationMessages'

export function AttachmentField({ node, onChange, fieldArgs }: FieldProps) {
  const fd = node.value.fieldDefinition as AttachmentFieldDefinition
  const formDef = node.value.formDefinition as FormFieldDefinition
  const fieldLabel = useFieldLabel(fd, formDef)
  const { moduleName } = useParams()

  const hasMandatoryFlag = nodeHasFlag(node, ControlValidationType.Mandatory)
  const hasMandatory = useHasMandatoryValidation(node, fd.entityName)

  // extract filename with extension
  const re = /[ \w()-]+\.[a-zA-Z0-9]+/

  const acceptedFileTypes = fd.accept

  const t = useTranslation()
  const api = useApiClient()
  const [value, setValue] = useFieldValue(node, onChange)
  const fileInRef = useRef<HTMLInputElement>(null)

  const handleClean = () => {
    node.value.isDirty = true
    setValue(null)
  }

  const isButtonVisible = (): boolean => {
    return fd?.canDownload && !!(value as AttachmentValue)?.url
  }

  const onFieldChange = (e: ChangeEvent<HTMLInputElement> | undefined) => {
    if (e?.target.files) {
      const file = e?.target.files[0]
      const fileName = re.exec(e?.target.value)?.pop() ?? ''
      const fieldValue = file ? new AttachmentValue(file, fileName) : null
      node.value.isDirty = true
      setValue(fieldValue)
    } else {
      return
    }
  }

  const isReadOnly = node.value.state === 'Readonly' || fieldArgs?.formOpenMode === FormOpenMode.Readonly
  const severity = useFieldSeverity(fd, node)

  return (
    <FormField
      required={(hasMandatory || hasMandatoryFlag) && fieldArgs?.formOpenMode !== FormOpenMode.Readonly}
      size="small"
      data-test-id={getTagFromNode(fd, moduleName)}
      severity={severity}
      readOnly={isReadOnly}
      disabled={isReadOnly}
    >
      {fieldArgs?.showLabel && <FormFieldLabel>{fieldLabel}</FormFieldLabel>}

      <input
        style={{ display: 'none' }}
        type="file"
        accept={acceptedFileTypes}
        onChange={onFieldChange}
        ref={fileInRef}
      />
      <TextInput
        disabled
        label={fieldArgs?.showLabel ? fieldLabel : undefined}
        value={(value as AttachmentValue)?.originalName ?? ''}
        endAdornment={
          <InputAdornment position="end">
            {value && !isReadOnly && (
              <ButtonIcon
                color="primary"
                aria-label="Clear"
                onClick={() => {
                  handleClean()
                }}
                edge="end"
              >
                <CloseIcon />
              </ButtonIcon>
            )}
            {!value && !isReadOnly && (
              <ButtonIcon
                color="primary"
                aria-label="Upload"
                onClick={() => {
                  fileInRef.current?.click()
                }}
                edge="end"
              >
                <FileUploadOutlinedIcon />
              </ButtonIcon>
            )}
            {isButtonVisible() && (
              <ButtonIcon
                color="primary"
                aria-label="Download"
                onClick={() => {
                  downloadAttachment(value as AttachmentValue, api)
                }}
                edge="end"
              >
                <FileDownloadOutlinedIcon />
              </ButtonIcon>
            )}
          </InputAdornment>
        }
      ></TextInput>

      <ValidationMessages messages={node.value.validationMessages} fieldDef={fd} node={node} />
    </FormField>
  )
}
